import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react'
import {
  onAuthStateChanged,
  Unsubscribe,
  User as FirebaseAuthUser
} from 'firebase/auth'
import { doc, onSnapshot, setDoc } from 'firebase/firestore'

import { auth, firestore } from '..'
import Loading from '../views/loading'

interface AuthContext {
  user: User | null
  admin: boolean
  coach: boolean
  setUser: Dispatch<SetStateAction<User | null>>
  setAdmin: Dispatch<SetStateAction<boolean>>
}

export interface User {
  uid: string
  displayName: string
  email: string
  phoneNumber: string
  photoURL: string
  theme: 'light' | 'dark'
  credits: number
  isAdmin: boolean
  isCoach: boolean
}

const Auth = createContext({} as AuthContext)

function buildUserProfile(user: FirebaseAuthUser): User {
  return {
    uid: user.uid,
    displayName: user.displayName ?? user.email!,
    email: user.email!,
    phoneNumber: user.phoneNumber ?? '',
    photoURL: user.photoURL ?? '',
    theme: 'light',
    credits: 0,
    isAdmin: false,
    isCoach: false,
  }
}

export default function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState<User | null>(null)
  const [admin, setAdmin] = useState(false)
  const [coach, setCoach] = useState(false)

  useEffect(() => {
    let unsub: Unsubscribe
    let userData: User | null = null

    onAuthStateChanged(auth, async (u) => {
      if (u) {
        const userRef = doc(firestore, 'users', u.uid)

        unsub = onSnapshot(
          userRef,
          async (snapshot) => {
            if (snapshot.exists()) {
              userData = {
                ...buildUserProfile(u),
                uid: snapshot.id,
                ...snapshot.data(),
              } as User
            } else {
              userData = buildUserProfile(u)

              await setDoc(userRef, {
                displayName: userData.displayName,
                email: userData.email,
                phoneNumber: userData.phoneNumber,
                photoURL: userData.photoURL,
                theme: userData.theme,
                credits: userData.credits,
                isAdmin: userData.isAdmin,
                isCoach: userData.isCoach,
              })
            }

            setUser(userData)
            setAdmin(!!userData?.isAdmin)
            setCoach(!!userData?.isCoach)
            setLoading(false)
          },
          async (_) => {
            userData = buildUserProfile(u)

            setUser(userData)
            setAdmin(false)
            setCoach(false)
            setLoading(false)
          }
        )
      } else {
        setUser(null)
        setAdmin(false)
        setCoach(false)
        setLoading(false)
      }
    })

    return () => {
      unsub && unsub()
      setUser(null)
      setAdmin(false)
    }
    // eslint-disable-next-line
  }, [])

  return (
    <Auth.Provider
      value={{
        user,
        admin,
        coach,
        setUser,
        setAdmin,
      }}>
      {!loading ? children : <Loading />}
    </Auth.Provider>
  )
}

export const useAuth = () => useContext(Auth)
